import { useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import { InputBarTargetComposer } from "@/components/panels/input-bar-target-composer";
import { standaloneBarDropAnchor } from "@/components/panels/standalone-bar-placement";
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import { submitInputBarTarget } from "@/lib/input-bar-target-submit";
import type { WorkspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { useCanvasStore } from "@/stores/canvas-store";
import { useStandaloneComposerDraft } from "@/stores/standalone-composer-draft-store";
import type {
  CursorInputSubmitMode,
  SummonSelection,
} from "@/stores/canvas-store";
import { useCursorInputShown } from "@/desktop/cursor-input-presence";
import { useStandaloneInputDrag } from "@/desktop/use-standalone-input-drag";

/** The main bar's dictation id: the same for every target it shows. */
const MAIN_BAR_DICTATION_ID = "input-bar-target:main";

interface CursorInputPosition {
  flowX: number;
  flowY: number;
  placement?: "cursor" | "anchor" | "bottom";
  selection?: SummonSelection;
  agentKey?: string;
  submitMode?: CursorInputSubmitMode;
  submitEventName?: string;
}

interface WorkspaceCursorInputLayerProps {
  /** The view the anchor's workspace point belongs to; key the layer by its kind. */
  adapter: WorkspaceViewAdapter;
  cursorInput: CursorInputPosition;
  cursorInputCtx: CursorInputContextValue;
  value: string;
  onChange: Dispatch<SetStateAction<string>>;
  onClose: () => void;
}

/**
 * The web summon bar (and the desktop workspace's own bar) in the workspace
 * layer root, above the agent cursors. Its anchor is a workspace point:
 * flow coordinates on the canvas, client coordinates on the wall.
 */
export function WorkspaceCursorInputLayer({
  adapter,
  cursorInput,
  cursorInputCtx,
  value,
  onChange,
  onClose,
}: WorkspaceCursorInputLayerProps) {
  // Mounted exactly while the bar shows.
  useCursorInputShown(true);
  // The anchor follows every pan and zoom; equal transforms do not re-render.
  const viewport = adapter.useTransform();
  const { updateDraft } = useStandaloneComposerDraft();
  const latestValueRef = useRef(value);
  latestValueRef.current = value;
  // The anchor is a workspace point: a drop pins the bar's corner there, so
  // it stays exactly where it was dropped and then follows pans and zooms.
  const drag = useStandaloneInputDrag(null, true, (drop) => {
    const current = useCanvasStore.getState().cursorInput;
    const pane = drag.containerRef.current?.getBoundingClientRect();
    if (!current || !pane) return;
    const point = standaloneBarDropAnchor({
      corner: { x: drop.left - pane.left, y: drop.top - pane.top },
      shellHeight: drop.height,
      // The standalone bar places itself against the window.
      viewport: { width: window.innerWidth, height: window.innerHeight },
    });
    useCanvasStore.getState().moveCursorInput({
      ...current,
      flowX: (point.x - viewport.x) / viewport.zoom,
      flowY: (point.y - viewport.y) / viewport.zoom,
      placement: "anchor",
    });
  });

  return (
    <div
      className="pointer-events-none absolute inset-0 z-[70] overflow-hidden"
      data-testid="cursor-input-layer"
    >
      <div
        ref={drag.containerRef}
        className="pointer-events-none absolute inset-0"
        {...drag.dragHandlers}
      >
        <InputBarTargetComposer
          variant="standalone"
          className="pointer-events-auto"
          dictationId={MAIN_BAR_DICTATION_ID}
          suppressPrediction={
            cursorInput.submitMode === "onboarding_demo_suppress"
          }
          cursorAnchor={{
            type: "flow",
            flowX: cursorInput.flowX,
            flowY: cursorInput.flowY,
            viewportX: viewport.x,
            viewportY: viewport.y,
            zoom: viewport.zoom,
          }}
          cursorPlacement={cursorInput.placement ?? "cursor"}
          compact
          targetPicker
          defaultAgentName="Denker"
          onClose={onClose}
          value={value}
          onChange={onChange}
          onSubmit={async () => {
            const submittedValue = value;
            if (cursorInput.submitMode === "onboarding_demo_suppress") {
              if (cursorInput.submitEventName) {
                window.dispatchEvent(new Event(cursorInput.submitEventName));
              }
              updateDraft(
                { text: "", chip: null, previousRoute: null },
                submittedValue,
              );
              onClose();
              return true;
            } else {
              const sent = await submitInputBarTarget(
                cursorInputCtx,
                value,
                cursorInput.agentKey,
              );
              if (sent === false) return false;
            }
            if (latestValueRef.current !== submittedValue) return true;
            updateDraft(
              { text: "", chip: null, previousRoute: null },
              submittedValue,
            );
            onClose();
            return true;
          }}
          isStreaming={cursorInputCtx.isStreaming}
          onStop={cursorInputCtx.onStop}
          agents={cursorInputCtx.inputAgents}
          selectedAgentIds={cursorInputCtx.selectedAgentIds}
          onSelectAgents={cursorInputCtx.onSelectAgents}
          projectId={cursorInputCtx.projectId}
          spaceId={cursorInputCtx.spaceId}
          onOpenSettings={cursorInputCtx.onOpenSettings}
          onOpenSettingsForAgent={cursorInputCtx.onOpenSettingsForAgent}
          onRemoveAgent={cursorInputCtx.onRemoveAgent}
          images={cursorInputCtx.attachedImages}
          onRemoveImage={cursorInputCtx.onRemoveImage}
          onImageFile={cursorInputCtx.onImageFile}
          slashCommands={cursorInputCtx.slashCommands}
          maxParallelAgents={cursorInputCtx.maxParallelAgents}
          onChangeMaxParallelAgents={cursorInputCtx.onChangeMaxParallelAgents}
          onAgentInspect={cursorInputCtx.onAgentInspect}
          focusedAgent={cursorInputCtx.focusedAgentInfo}
          providers={cursorInputCtx.providers}
          onUpdateAgentModel={cursorInputCtx.onUpdateAgentModel}
          onUpdateAgentEffort={cursorInputCtx.onUpdateAgentEffort}
          companionDisconnected={cursorInputCtx.companionDisconnected}
          onFixCompanion={cursorInputCtx.onFixCompanion}
        />
      </div>
    </div>
  );
}
